import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Statistic, Table, Typography, Spin, Alert } from 'antd';
import { GlobalOutlined, FileTextOutlined, FormOutlined, UserOutlined, EyeOutlined } from '@ant-design/icons';
import { useAuth } from '../contexts/AuthContext';

const { Title } = Typography;

// 模拟数据
const mockStats = {
  websites: 5,
  contents: 12,
  forms: 3,
  users: 2,
  visits: 324
};

const mockWebsites = [
  { id: '1', domain: 'example1.com', title: '示例网站1', updated_at: '2023-05-10T12:00:00Z' },
  { id: '2', domain: 'example2.com', title: '示例网站2', updated_at: '2023-05-11T10:30:00Z' },
  { id: '3', domain: 'example3.com', title: '示例网站3', updated_at: '2023-05-12T14:15:00Z' },
];

const mockContents = [
  { id: '1', title: '关于我们', type: 'page', updated_at: '2023-05-10T12:00:00Z' },
  { id: '2', title: '产品介绍', type: 'page', updated_at: '2023-05-11T10:30:00Z' },
  { id: '3', title: '最新动态', type: 'post', updated_at: '2023-05-12T14:15:00Z' },
];

const mockSubmissions = [
  { id: '1', form_id: '1', created_at: '2023-05-10T12:00:00Z', ip_address: '192.168.1.1' },
  { id: '2', form_id: '1', created_at: '2023-05-11T10:30:00Z', ip_address: '192.168.1.2' },
  { id: '3', form_id: '2', created_at: '2023-05-12T14:15:00Z', ip_address: '192.168.1.3' },
];

const mockIpSources = [
  { country: '中国', count: 120 },
  { country: '美国', count: 89 },
  { country: '日本', count: 45 },
];

const Dashboard: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [stats, setStats] = useState(mockStats);
  const [recentWebsites, setRecentWebsites] = useState(mockWebsites);
  const [recentContents, setRecentContents] = useState(mockContents);
  const [recentSubmissions, setRecentSubmissions] = useState(mockSubmissions);
  const [ipSources, setIpSources] = useState(mockIpSources);
  
  const { user } = useAuth();
  
  useEffect(() => {
    // 模拟加载数据
    setTimeout(() => {
      setLoading(false);
    }, 500);
  }, []);
  
  const websiteColumns = [
    {
      title: '域名',
      dataIndex: 'domain',
      key: 'domain',
    },
    {
      title: '标题',
      dataIndex: 'title',
      key: 'title',
      ellipsis: true,
    },
    {
      title: '更新时间',
      dataIndex: 'updated_at',
      key: 'updated_at',
      render: (text: string) => new Date(text).toLocaleString('zh-CN'),
    },
  ];
  
  const contentColumns = [
    {
      title: '标题',
      dataIndex: 'title',
      key: 'title',
      ellipsis: true,
    },
    {
      title: '类型',
      dataIndex: 'type',
      key: 'type',
      render: (text: string) => {
        const types = {
          page: '页面',
          post: '文章',
          product: '产品',
        };
        return types[text as keyof typeof types] || text;
      },
    },
    {
      title: '更新时间',
      dataIndex: 'updated_at',
      key: 'updated_at',
      render: (text: string) => new Date(text).toLocaleString('zh-CN'),
    },
  ];
  
  const submissionColumns = [
    {
      title: 'IP地址',
      dataIndex: 'ip_address',
      key: 'ip_address',
    },
    {
      title: '提交时间',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (text: string) => new Date(text).toLocaleString('zh-CN'),
    },
  ];
  
  const ipSourceColumns = [
    {
      title: '国家/地区',
      dataIndex: 'country',
      key: 'country',
    },
    {
      title: '访问次数',
      dataIndex: 'count',
      key: 'count',
    },
  ];

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" tip="加载仪表板数据..." />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '20px' }}>
        <Alert
          message="错误"
          description={error}
          type="error"
          showIcon
        />
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      <Title level={2}>仪表板</Title>
      
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} md={8} lg={6}>
          <Card>
            <Statistic
              title="网站数量"
              value={stats.websites}
              prefix={<GlobalOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8} lg={6}>
          <Card>
            <Statistic
              title="内容数量"
              value={stats.contents}
              prefix={<FileTextOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8} lg={6}>
          <Card>
            <Statistic
              title="表单数量"
              value={stats.forms}
              prefix={<FormOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8} lg={6}>
          <Card>
            <Statistic
              title="7天访问量"
              value={stats.visits}
              prefix={<EyeOutlined />}
            />
          </Card>
        </Col>
      </Row>
      
      <Row gutter={[16, 16]} style={{ marginTop: '20px' }}>
        <Col span={24} lg={12}>
          <Card title="最近网站">
            <Table
              dataSource={recentWebsites}
              columns={websiteColumns}
              rowKey="id"
              pagination={false}
              size="small"
            />
          </Card>
        </Col>
        <Col span={24} lg={12}>
          <Card title="最近内容">
            <Table
              dataSource={recentContents}
              columns={contentColumns}
              rowKey="id"
              pagination={false}
              size="small"
            />
          </Card>
        </Col>
      </Row>
      
      <Row gutter={[16, 16]} style={{ marginTop: '20px' }}>
        <Col span={24} lg={12}>
          <Card title="表单提交">
            <Table
              dataSource={recentSubmissions}
              columns={submissionColumns}
              rowKey="id"
              pagination={false}
              size="small"
            />
          </Card>
        </Col>
        <Col span={24} lg={12}>
          <Card title="访问来源">
            <Table
              dataSource={ipSources}
              columns={ipSourceColumns}
              rowKey="country"
              pagination={false}
              size="small"
            />
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default Dashboard; 